import { cx } from '@emotion/css';
import type { CSSProperties, FC, KeyboardEvent, MouseEvent, ReactNode } from 'react';

import type { BaseComponentProps } from '../../types';
import { dataSetToAttributes } from '../../utils';
import {
  directionCssMap,
  directionalOverlayBackdropActiveCss,
  directionalOverlayBackdropBaseCss,
  directionalOverlayPanelActiveCss,
  directionalOverlayPanelCss,
} from './DirectionalOverlay.styles';

/**
 * A low-level fixed panel that slides in from any edge of the screen.
 *
 * Use `DirectionalOverlay` when you need full control over the slide direction, sizing, content
 * structure, or backdrop visibility — or when building a higher-level overlay component on top of
 * this primitive.
 *
 * Prefer {@link SideOverlay} instead when you need:
 * - A ready-made drawer with a close button, title/body layout, and Motif theming.
 * - Automatic RTL direction flipping.
 * - Mobile-specific behaviour (full-width bottom sheet on mobile, side panel on desktop).
 *
 * @example
 * // Custom bottom sheet on desktop, full-height on mobile
 * <DirectionalOverlay
 *   isOpen={isOpen}
 *   desktopDirection="bottom"
 *   mobileDirection="bottom"
 *   desktopSizePercent={40}
 *   mobileSizePercent={60}
 *   onBackdropClick={close}
 * >
 *   <MyContent />
 * </DirectionalOverlay>
 *
 * @example
 * // Left-side panel without a backdrop
 * <DirectionalOverlay
 *   isOpen={isOpen}
 *   desktopDirection="left"
 *   desktopSizePercent={30}
 *   showBackdrop={false}
 * >
 *   <nav>…</nav>
 * </DirectionalOverlay>
 */
export type OverlaySlideDirection = 'top' | 'right' | 'bottom' | 'left';

export interface DirectionalOverlayProps extends BaseComponentProps {
  isOpen: boolean;
  children: ReactNode;
  /** Slide origin for non-mobile breakpoints. */
  desktopDirection: OverlaySlideDirection;
  /** Slide origin for mobile; defaults to {@link desktopDirection}. */
  mobileDirection?: OverlaySlideDirection;
  /** Viewport share (0–100) on non-mobile: width (vw) for left/right, height (vh) for top/bottom. */
  desktopSizePercent: number;
  /** Viewport share (0–100) on mobile; defaults to {@link desktopSizePercent}. */
  mobileSizePercent?: number;
  onBackdropClick?: (event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>) => void;
  /** Whether to render the backdrop on both desktop and mobile. Defaults to `true`. */
  showBackdrop?: boolean;
  backdropClassName?: string;
  /**
   * Merged onto the backdrop only while `isOpen` is true (e.g. `opacity: 1` for a fade in).
   * Wins over `backdropClassName` so open-state rules take precedence.
   */
  backdropOpenClassName?: string;
}

export const DirectionalOverlay: FC<DirectionalOverlayProps> = ({
  isOpen,
  children,
  desktopDirection,
  mobileDirection,
  desktopSizePercent,
  mobileSizePercent = desktopSizePercent,
  onBackdropClick,
  showBackdrop = true,
  backdropClassName,
  backdropOpenClassName,
  className,
  style,
  dataset,
  'data-testid': dataTestId,
}) => {
  const resolvedMobileDirection = mobileDirection ?? desktopDirection;
  const resolvedMobileSizePercent = mobileSizePercent ?? desktopSizePercent;

  const panelStyle: CSSProperties = {
    ...style,
    '--directional-overlay-desktop-size-pct': desktopSizePercent,
    '--directional-overlay-mobile-size-pct': resolvedMobileSizePercent,
  } as CSSProperties;

  const handleBackdropKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onBackdropClick?.(event);
    }
  };

  const desktopCss = directionCssMap[desktopDirection];
  const mobileCss = directionCssMap[resolvedMobileDirection];

  const backdropClass = cx(
    directionalOverlayBackdropBaseCss,
    isOpen && directionalOverlayBackdropActiveCss,
    backdropClassName,
    isOpen && backdropOpenClassName
  );

  const panelClass = cx(
    directionalOverlayPanelCss,
    desktopCss.desktop,
    mobileCss.mobile,
    isOpen && directionalOverlayPanelActiveCss,
    isOpen && desktopCss.desktopOpen,
    isOpen && mobileCss.mobileOpen,
    className
  );

  return (
    <>
      {showBackdrop && (
        <div
          className={backdropClass}
          onClick={onBackdropClick}
          onKeyDown={handleBackdropKeyDown}
          role="presentation"
        />
      )}
      <aside
        {...dataSetToAttributes(dataset)}
        className={panelClass}
        data-testid={dataTestId}
        style={panelStyle}
      >
        {children}
      </aside>
    </>
  );
};

DirectionalOverlay.displayName = 'DirectionalOverlay';
